import React, { useMemo } from "react";
import { useParams } from 'react-router-dom';
import { useStytchB2BClient, useStytchMemberSession } from '@stytch/react/b2b';
// Icons removed from table headers for cleaner design
import { useTimeRange } from '../contexts/TimeRangeContext';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { useApp } from '../contexts/AppContext';
import { isInDemoMode } from '../services/demoDataService';
import { useAgentList } from '../services/agentsApi';
import { TabbedPageLayout, ErrorState } from '../components';
import JourneySankey from '../components/JourneySankey';

export default function Agents({ embedded = false }) {
  const { selectedAppId } = useParams(); // Extract from /app/:selectedAppId/agents route
  const stytchClient = useStytchB2BClient();
  const { session } = useStytchMemberSession();
  const { displayLabel, selectedRange } = useTimeRange();
  const { selectedEnvironment, environments } = useEnvironment();
  const { selectedAppName } = useApp();
  const tenantId = session?.organization_id;
  const isDemo = isInDemoMode(tenantId, selectedEnvironment, selectedAppId);

  // Resolve app name for breadcrumb (follow Sessions.js pattern)
  const appNameForBreadcrumb = useMemo(() => {
    // 1. Always check environments cache first (most reliable when switching apps)
    if (selectedAppId && environments?.length) {
      for (const env of environments) {
        const app = env.apps?.find(a => a.id === selectedAppId);
        if (app) return app.name;
      }
    }

    // 2. Fallback to selectedAppName from AppContext
    if (selectedAppName) return selectedAppName;

    // 3. Last resort: use ID
    return selectedAppId;
  }, [selectedAppId, environments, selectedAppName]);

  // Breadcrumb: Home → App Name → Journeys
  const breadcrumbItems = useMemo(() => {
    const params = new URLSearchParams();
    if (selectedEnvironment) params.set('env', selectedEnvironment);
    if (selectedRange) params.set('range', selectedRange);
    const queryString = params.toString() ? `?${params.toString()}` : '';

    return [
      { label: appNameForBreadcrumb, to: `/app/${selectedAppId}${queryString}` },
      { label: 'Business Tasks' },
    ];
  }, [appNameForBreadcrumb, selectedAppId, selectedEnvironment, selectedRange]);

  const { flowData, loading, error } = useAgentList(selectedAppId, 'agents', isDemo);

  if (error && !isDemo) {
    return <ErrorState title="Error Loading Business Tasks" message={error} />;
  }

  // Embedded under the Journeys sub-tab row: reclaim the Sankey's top whitespace
  // so the pill row costs no net canvas height (top edge stays ≈ Telemetry's table).
  const sankeyContent = (
    <div className="h-full flex flex-col gap-2">
      <div className="flex-1 min-h-0">
        <JourneySankey data={flowData} loading={loading} error={error} topMargin={embedded ? 8 : 40} />
      </div>
    </div>
  );

  if (embedded) {
    return sankeyContent;
  }

  return (
    <TabbedPageLayout
      breadcrumbItems={breadcrumbItems}
      title="Business Tasks"
      stats={null}
      filters={null}
      loading={loading}
      error={error}
      info={null}
      data={null}
    >
      {sankeyContent}
    </TabbedPageLayout>
  );
}
